import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Row, Form, Input, Select, InputNumber, Button, Typography, message, Spin, Checkbox, Modal } from "antd";
import axios from "axios";
import styles from "./GetAQuote.module.scss";
import { apiUrl, BASE_URL } from "../../Constant";
import { getUsdToGbp } from "../../utils/fxRate";

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

let _deductionsCache = null;

const fetchDeductions = async () => {
  if (_deductionsCache) return _deductionsCache;
  try {
    const res = await axios.get(`${apiUrl.GET_METAL_PRICES.replace("/metal/public/prices", "/sell-gold/get")}`);
    _deductionsCache = res?.data?.deductions || {};
    return _deductionsCache;
  } catch {
    return {};
  }
};

const applyDeduction = (price, caratKey, deductions) => {
  const pct = Number(deductions?.[caratKey] || 0);
  if (pct <= 0) return price;
  return Number((price * (1 - pct / 100)).toFixed(2));
};

const fallbackPrices = [
  { carat: "9K", purity: "37.5%", pricePerGram: 27.50 },
  { carat: "10K", purity: "41.7%", pricePerGram: 30.60 },
  { carat: "14K", purity: "58.3%", pricePerGram: 42.80 },
  { carat: "18K", purity: "75.0%", pricePerGram: 55.10 },
  { carat: "21K", purity: "87.5%", pricePerGram: 64.20 },
  { carat: "22K", purity: "91.6%", pricePerGram: 67.30 },
  { carat: "24K", purity: "99.9%", pricePerGram: 73.40 },
];

const purityMap = {
  9: { label: "9K", percent: "37.5%" },
  10: { label: "10K", percent: "41.7%" },
  14: { label: "14K", percent: "58.3%" },
  18: { label: "18K", percent: "75.0%" },
  21: { label: "21K", percent: "87.5%" },
  22: { label: "22K", percent: "91.6%" },
  24: { label: "24K", percent: "99.9%" },
};

function GetAQuote() {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [goldWeight, setGoldWeight] = useState(null);
  const [purity, setPurity] = useState(null);
  const [itemType, setItemType] = useState(null);
  const [weights, setWeights] = useState({});
  const [goldPrices, setGoldPrices] = useState(fallbackPrices);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [termsOpen, setTermsOpen] = useState(false);
  const userToken = useSelector((state) => state.user?.token);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchMetalPrices();

    // Auto-populate form with logged-in user details
    if (userToken) {
      const fetchProfile = async () => {
        try {
          const res = await axios.get(`${BASE_URL}/user/profile/get`, { headers: { Authorization: userToken } });
          const profile = res?.data?.user || res?.data;
          if (profile) {
            form.setFieldsValue({
              name: profile.User_Name || "",
              email: profile.User_Email || "",
              phone: profile.User_Mobile_No ? String(profile.User_Mobile_No) : "",
              company: profile.Company || "",
            });
          }
        } catch (e) { /* ignore */ }
      };
      fetchProfile();
    }
  }, [userToken]);

  const fetchMetalPrices = async () => {
    try {
      setPricesLoading(true);
      const [res, deductions, fxRate] = await Promise.all([
        axios.get(apiUrl.GET_METAL_PRICES),
        fetchDeductions(),
        getUsdToGbp(),
      ]);
      const fx = fxRate || 1;
      if (res.data?.type === "success" && res.data.metals?.length > 0) {
        const gold = res.data.metals.find(
          (m) => m.metalName?.toLowerCase() === "gold"
        );
        if (gold && gold.baseRate && gold.basePurity) {
          const targetPurities = [9, 10, 14, 18, 21, 22, 24];
          const prices = targetPurities.map((p) => {
            const purityRate = gold.purityRates?.find(
              (pr) => Number(pr.purity) === p
            );
            const ratePerGramUsd = purityRate
              ? Number(purityRate.ratePerGram)
              : Number(gold.baseRate) * (p / Number(gold.basePurity));
            const ratePerGram = ratePerGramUsd * fx;
            const caratKey = purityMap[p]?.label || `${p}K`;
            return {
              carat: caratKey,
              purity: purityMap[p]?.percent || "",
              pricePerGram: applyDeduction(Number(ratePerGram.toFixed(2)), caratKey, deductions),
            };
          });
          setGoldPrices(prices);
          setLastUpdated(gold.updatedAt);
        }
      }
    } catch (err) {
      // Keep fallback prices
    } finally {
      setPricesLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSubmitting(true);

      const selectedPurity = values.purity;
      const matchedPrice = selectedPurity
        ? goldPrices.find((p) => p.carat.toLowerCase() === selectedPurity.replace("k", "K").toUpperCase())
        : null;

      // Plain text: the contact email shows a message as text (line breaks
      // kept), never as HTML, so tags here would arrive as literal "<b>".
      const msg =
        `Item Type: ${values.itemType || "N/A"}\n` +
        `Purity: ${selectedPurity || "N/A"}` +
        (matchedPrice ? ` (£${matchedPrice.pricePerGram.toFixed(2)}/gram at time of enquiry)` : "") + `\n` +
        `Approximate Weight: ${values.weight ? values.weight + "g" : "N/A"}` +
        (matchedPrice && values.weight ? ` — Est. Value: £${(values.weight * matchedPrice.pricePerGram).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "") + `\n\n` +
        `Description:\n${values.description || "N/A"}\n\n` +
        `Live gold prices at time of submission:\n` +
        goldPrices.map((p) => `${p.carat}: £${p.pricePerGram.toFixed(2)}/gram`).join("  |  ");

      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        subject: "Sell Gold – Quote Request",
        message: msg,
      };

      const res = await axios.post(apiUrl.ADD_CONTACT_US, payload);
      if (res?.data?.type === "success") {
        message.success("Thank you! We'll be in touch shortly with your quote.");
        form.resetFields();
        setGoldWeight(null);
        setPurity(null);
      } else {
        message.error(res?.data?.message || "Failed to send. Please try again.");
      }
    } catch (error) {
      message.error("Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.eyebrow}>Sell Your Gold</span>
        <Title level={1} className={styles.pageTitle}>Get A Quote</Title>
        <div className={styles.headingLine} />
      </div>

      {/* Intro */}
      <div className={styles.intro}>
        <Paragraph className={styles.introText}>
          Looking to sell your gold? We offer competitive, transparent pricing based on
          live market rates. Fill in the form below and our team will provide you with
          a no-obligation quote within 24 hours.
        </Paragraph>
      </div>

      {/* How It Works */}
      <div className={styles.stepsSection}>
        <div className={styles.stepsInner}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <h4 className={styles.stepTitle}>Submit Details</h4>
            <p className={styles.stepDesc}>Tell us about your gold items using the form below.</p>
          </div>
          <div className={styles.stepConnector} />
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <h4 className={styles.stepTitle}>Receive Quote</h4>
            <p className={styles.stepDesc}>We'll send you a competitive quote within 24 hours.</p>
          </div>
          <div className={styles.stepConnector} />
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <h4 className={styles.stepTitle}>Get Paid</h4>
            <p className={styles.stepDesc}>Accept the offer and receive instant payment.</p>
          </div>
        </div>
      </div>

      {/* Gold Price Calculator */}
      <div className={styles.calculatorSection}>
        <Row justify="center">
          <Col xs={22} md={20} lg={16} xl={14}>
            <div className={styles.calculatorCard}>
              <h3 className={styles.formTitle}>Live Gold Prices</h3>
              <div className={styles.formTitleLine} />
              {lastUpdated && (
                <p className={styles.lastUpdated}>
                  Last updated: {new Date(lastUpdated).toLocaleDateString("en-GB", {
                    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                  })}
                </p>
              )}
              <p className={styles.calculatorNote}>
                Select your item type and enter the weight of your gold to see an estimated value. Prices are updated daily from live market data.
              </p>

              <div className={styles.itemTypeSelect}>
                <label className={styles.itemTypeLabel}>Item Type</label>
                <Select
                  placeholder="Select item type"
                  value={itemType}
                  onChange={setItemType}
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Select.Option value="gold-jewellery">Gold Jewellery</Select.Option>
                  <Select.Option value="gold-coins">Gold Coins</Select.Option>
                  <Select.Option value="gold-bars">Gold Bars / Bullion</Select.Option>
                  <Select.Option value="scrap-gold">Scrap Gold</Select.Option>
                  <Select.Option value="other">Other</Select.Option>
                </Select>
              </div>

              <div className={styles.tableWrap}>
                {pricesLoading ? (
                  <div className={styles.tableLoader}><Spin /></div>
                ) : null}
                <table className={styles.priceTable}>
                  <thead>
                    <tr>
                      <th>Carat</th>
                      <th>Price / Gram</th>
                      <th>Weight (g)</th>
                      <th>Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goldPrices.map((row) => {
                      const w = weights[row.carat] || 0;
                      const total = (w * row.pricePerGram).toFixed(2);
                      return (
                        <tr key={row.carat}>
                          <td className={styles.caratCell}>{row.carat}</td>
                          <td className={styles.priceCell}>
                            £{row.pricePerGram.toFixed(2)}
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="0"
                              className={styles.weightInput}
                              value={weights[row.carat] || ""}
                              onChange={(e) =>
                                setWeights((prev) => ({
                                  ...prev,
                                  [row.carat]: parseFloat(e.target.value) || 0,
                                }))
                              }
                            />
                          </td>
                          <td className={styles.totalCell}>
                            {w > 0 ? `£${Number(total).toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {Object.values(weights).some((w) => w > 0) && (
                <div className={styles.grandTotal}>
                  <span>Estimated Total</span>
                  <span className={styles.grandTotalValue}>
                    £
                    {goldPrices
                      .reduce(
                        (sum, row) =>
                          sum + (weights[row.carat] || 0) * row.pricePerGram,
                        0
                      )
                      .toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      {/* Form */}
      <div className={styles.formSection}>
        <Row justify="center">
          <Col xs={22} md={20} lg={16} xl={14}>
            <div className={styles.formCard}>
              <h3 className={styles.formTitle}>Your Details</h3>
              <div className={styles.formTitleLine} />

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                size="large"
                className={styles.form}
              >
                <Row gutter={[20, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Full Name"
                      name="name"
                      rules={[{ required: true, message: "Please enter your name" }]}
                    >
                      <Input placeholder="John Doe" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Phone Number"
                      name="phone"
                      rules={[{ required: true, message: "Please enter your phone number" }]}
                    >
                      <Input placeholder="+44 7XXX XXX XXX" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Registered Business Name"
                  name="company"
                >
                  <Input placeholder="Your company name" />
                </Form.Item>

                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input placeholder="john@example.com" />
                </Form.Item>

                <div className={styles.formDivider} />
                <h3 className={styles.formTitle}>Gold Details</h3>
                <div className={styles.formTitleLine} />

                <Row gutter={[20, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Item Type"
                      name="itemType"
                      rules={[{ required: true, message: "Please select item type" }]}
                    >
                      <Select placeholder="Select type">
                        <Select.Option value="gold-jewellery">Gold Jewellery</Select.Option>
                        <Select.Option value="gold-coins">Gold Coins</Select.Option>
                        <Select.Option value="gold-bars">Gold Bars / Bullion</Select.Option>
                        <Select.Option value="scrap-gold">Scrap Gold</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Purity (Carat)"
                      name="purity"
                    >
                      <Select
                        placeholder="Select purity"
                        value={purity}
                        onChange={setPurity}
                      >
                        <Select.Option value="24k">24K (99.9%)</Select.Option>
                        <Select.Option value="22k">22K (91.6%)</Select.Option>
                        <Select.Option value="21k">21K (87.5%)</Select.Option>
                        <Select.Option value="18k">18K (75.0%)</Select.Option>
                        <Select.Option value="14k">14K (58.3%)</Select.Option>
                        <Select.Option value="10k">10K (41.7%)</Select.Option>
                        <Select.Option value="9k">9K (37.5%)</Select.Option>
                        <Select.Option value="unknown">Not Sure</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Approximate Weight (grams)"
                      name="weight"
                    >
                      <InputNumber
                        placeholder="e.g. 10"
                        min={0.1}
                        value={goldWeight}
                        onChange={setGoldWeight}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Description"
                  name="description"
                >
                  <TextArea
                    rows={4}
                    placeholder="Describe your items — condition, markings, hallmarks, quantity, etc."
                  />
                </Form.Item>

                <Form.Item
                  name="agree"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject("You must agree to the terms and conditions"),
                    },
                  ]}
                >
                  <Checkbox className={styles.disclaimer}>
                    I agree to the{" "}
                    <a href="#" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}>
                      terms and conditions
                    </a>
                  </Checkbox>
                </Form.Item>
                <p className={styles.disclaimerText}>
                  By submitting this form, you acknowledge that all quotes provided by The Diamond Counter
                  are estimates based on the information you supply and current market rates. Final
                  valuations may differ following physical inspection of items. The Diamond Counter reserves
                  the right to adjust or withdraw any quote. Submission of this form does not constitute a
                  binding agreement to buy or sell.
                </p>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={submitting}
                    className={styles.submitBtn}
                  >
                    Request My Quote
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        </Row>
      </div>

      {/* Trust Points */}
      <div className={styles.trustSection}>
        <div className={styles.trustGrid}>
          <div className={styles.trustItem}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.8">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            <span>Secure & Confidential</span>
          </div>
          <div className={styles.trustItem}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 20V9a5 5 0 0 1 10 0"/>
              <line x1="5" y1="12" x2="16" y2="12"/>
              <line x1="5" y1="16" x2="16" y2="16"/>
              <line x1="7" y1="20" x2="18" y2="20"/>
            </svg>
            <span>Best Market Rates</span>
          </div>
          <div className={styles.trustItem}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.8">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Instant Payment</span>
          </div>
          <div className={styles.trustItem}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.8">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>No Obligation</span>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <Modal
        open={termsOpen}
        onCancel={() => setTermsOpen(false)}
        footer={
          <button
            type="button"
            onClick={() => setTermsOpen(false)}
            style={{ width: "100%", height: 44, background: "#111", color: "#D4AF37", border: "none", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer" }}
          >
            I Understand
          </button>
        }
        title={null}
        centered
        width={640}
        zIndex={10001}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: "32px 32px 0" }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#111", margin: "0 0 8px" }}>Terms & Conditions</h3>
          <p style={{ fontSize: 11, color: "#C5A028", fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", margin: "0 0 20px" }}>Sell Your Gold Service</p>
          <div style={{ fontSize: 13, color: "#555", lineHeight: 2 }}>
            <p><strong>1. Quotation Estimates</strong><br />
            All quotes provided through this form are preliminary estimates only, based on the information you supply and current live market rates. These estimates are indicative and do not constitute a binding offer or guarantee of payment.</p>

            <p><strong>2. Physical Inspection Required</strong><br />
            Final valuation and any offer of payment will only be made after we have physically received, inspected, and verified your gold items at our premises. The final offer may differ from the initial estimate depending on the actual weight, purity, and condition of the items.</p>

            <p><strong>3. No Obligation</strong><br />
            Submitting a quote request does not obligate you to sell your gold, nor does it obligate The Diamond Counter to purchase it. Either party may withdraw at any time before a transaction is finalised.</p>

            <p><strong>4. Payment</strong><br />
            No payment will be made until the gold has been physically received, inspected, and a final offer has been accepted by you. Payment will be processed via the agreed method upon acceptance of the final valuation.</p>

            <p><strong>5. Shipping & Insurance</strong><br />
            If you choose to send your gold to us, you are responsible for ensuring adequate packaging and insurance during transit. The Diamond Counter is not liable for any loss or damage that occurs during shipping before the items are received at our premises.</p>

            <p><strong>6. Return of Items</strong><br />
            If you decline our final offer, we will return your items to you at our expense via insured delivery. Items will be returned in the same condition as received.</p>

            <p><strong>7. Data Protection</strong><br />
            Your personal information submitted through this form will be used solely for the purpose of processing your sell gold enquiry. We will not share your details with third parties without your consent.</p>

            <p><strong>8. Market Fluctuations</strong><br />
            Gold prices fluctuate continuously. The price per gram shown on this page reflects the rate at the time of viewing and may change before your items are received and valued.</p>

            <p style={{ borderTop: "1px solid #eee", paddingTop: 16, fontSize: 12, color: "#999" }}>
              By submitting a quote request, you confirm that you have read, understood, and agree to these terms and conditions. The Diamond Counter reserves the right to amend these terms at any time.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default GetAQuote;
